import moment, { Moment } from "moment";
import { utc } from "PFCore/helpers/date";
import { isIOS } from "PFCore/helpers/ios";
import { KeyboardEvent, MouseEvent, useEffect, useRef } from "react";
import { lpad } from "underscore.string";

import { getDayClass } from "./calendar_date.utils";

type Highlighted = boolean | boolean[];

type CalendarDateProps = {
  date: Moment;
  isCurrentMonth: boolean;
  matchMaxDate?: string;

  disabled?: boolean;
  selected?: boolean;
  keyboardSelected?: boolean;
  highlighted?: Highlighted;

  prevBusy?: boolean | string;
  nextBusy?: boolean | string;
  hovered?: boolean;
  readOnly?: boolean;

  handleClick?: (date: Moment, event: MouseEvent) => void;
  handleMouseEnter?: (date: Moment) => void;
  handleMouseLeave?: (date: Moment) => void;
  handleKeyDown?: (event: KeyboardEvent) => void;
};

const CalendarDate = ({
  date,
  handleClick,
  handleMouseLeave,
  handleMouseEnter,
  handleKeyDown,
  prevBusy,
  nextBusy,
  highlighted = false,
  hovered = false,
  disabled = false,
  selected = false,
  keyboardSelected,
  readOnly = false,
  isCurrentMonth,
  matchMaxDate
}: CalendarDateProps) => {
  const dateCellRef = useRef<HTMLSpanElement>(null);

  const isToday = date.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
  // NOTE: if you bind to mouseover ios simulates that with a click (you'd need to doubleclick to get a click)
  const _handleMouseLeave = isIOS() ? undefined : () => handleMouseLeave && handleMouseLeave(date);
  const _handleMouseEnter = isIOS() ? undefined : () => handleMouseEnter && handleMouseEnter(date);
  const _handleClick = (event: MouseEvent) => !disabled && handleClick && handleClick(date, event);
  const dateNumber = lpad(String(date.get("date")), 2, "0");

  const highlightedList: boolean[] = Array.isArray(highlighted) ? highlighted : [highlighted];

  const classConfig: Record<string, boolean | undefined> = {
    today: isToday,
    prevBusy: !!prevBusy,
    nextBusy: !!nextBusy,
    hovered,
    disabled,
    selected,
    keyboardSelected,
    readOnly,
    offLimit: !!matchMaxDate && date > utc(matchMaxDate),
    notCurrentMonth: !isCurrentMonth
  };

  useEffect(() => {
    if (keyboardSelected) {
      dateCellRef.current?.focus();
    }
  }, [keyboardSelected]);

  return (
    <span
      ref={dateCellRef}
      role="gridcell"
      aria-current={isToday ? "date" : undefined}
      tabIndex={selected || keyboardSelected ? 0 : -1}
      data-date={date.format("YYYY-MM-DD")}
      onClick={_handleClick}
      onKeyDown={handleKeyDown}
      onMouseLeave={_handleMouseLeave}
      onBlur={_handleMouseEnter}
      onMouseEnter={_handleMouseEnter}
      onFocus={_handleMouseEnter}
      className={getDayClass(classConfig)}
      data-highlighted={highlightedList}
      data-not-current-month={!isCurrentMonth}
    >
      <span>{dateNumber}</span>
    </span>
  );
};

export default CalendarDate;
